<script lang="ts">
  import type {
    LayoutComponent as LayoutComponentType,
    LayoutProps,
    LayoutName,
  } from "$lib/layouts/layoutTypes";
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";
  import defaultIconUrl from "$assets/org_icons/elev8-by-vertical-city-logo-full-colour-highres.png";
  import LayoutFullscreen from "./LayoutFullscreen.svelte";
  import LayoutWeatherBottom from "./LayoutWeatherBottom.svelte";
  import { blur } from "svelte/transition";
  import { quadInOut } from "svelte/easing";
  import LayoutKone from "./LayoutKone.svelte";

  interface Props extends LayoutProps {
    layout?: LayoutName;
  }
  const { layout, children, ...props }: Props = $props();

  const config = getConfigurationContext();
  const defaultLayout = LayoutFullscreen;
  let theme = $derived(config.screen?.theme ?? "default");
  let screenLayout = $derived(config.screen?.layout as LayoutName | undefined);
  let LayoutComponent = $derived(
    getLayoutComponent(theme, layout, screenLayout)
  );
  let logoUrl = $derived(config.screen?.property_logo_url ?? defaultIconUrl);
  let fullBleedLogo = $derived(config.screen?.full_bleed_logo ?? false);
  let visionEnabled = $derived(config.screen?.vision_enabled ?? false);

  // spot layout > config screen layout > theme-based fallback
  function getLayoutComponent(
    theme: string,
    spotLayout?: LayoutName,
    configLayout?: LayoutName
  ): LayoutComponentType {
    const layoutName = spotLayout ?? configLayout;
    if (layoutName && layoutNameToLayoutMap[layoutName]) {
      return layoutNameToLayoutMap[layoutName];
    }
    return themeToLayoutMap[theme] ?? defaultLayout;
  }

  const layoutNameToLayoutMap: Record<LayoutName, LayoutComponentType> = {
    fullscreen: LayoutFullscreen,
    landscape_weather_bottom: LayoutWeatherBottom,
    kone_weather: LayoutKone,
    default: defaultLayout,
  };

  const themeToLayoutMap: Record<string, LayoutComponentType> = {
    verticalcity: LayoutWeatherBottom,
    kone: LayoutKone,
    default: defaultLayout,
  };
</script>

{#key LayoutComponent}
  <div
    in:blur={{ duration: 50, delay: 150, easing: quadInOut, opacity: 0.5 }}
    out:blur={{ duration: 150, easing: quadInOut, opacity: 0.5 }}
    class="layout-selector"
  >
    <LayoutComponent {logoUrl} {fullBleedLogo} {visionEnabled} {...props}>
      {@render children?.()}
    </LayoutComponent>
  </div>
{/key}

<style>
  .layout-selector {
    width: 100%;
    height: 100%;
    display: flex;
    /* NOTE: LayoutComponent root can be flex item but doesn't have to */
  }
</style>
